import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import feather from 'feather-icons';
import { Link, useNavigate } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import Loader from '../components/Loader';
import Alert from '../components/Alert';
import BottomNavbar from '../components/Home/BottomNavbar';
import { useLanguage } from '../context/LanguageContext';
import PWAInstallSettingsSection from '../components/Modals/PWAInstallSettingsSection';
import { getPageMeta } from '../util/seoHelper';
import useRefreshSignal from '../hooks/useRefreshSignal';

function SettingPage() {
    const navigate = useNavigate();
    const refreshSignal = useOutletRefreshSignal();
    const { t } = useLanguage();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState(null);
    const [notification, setNotification] = useState(
        settings === null ? true : settings?.notifications
    );
    const { language, changeLanguage } = useLanguage();

    const fetchUserSettings = useCallback(async () => {
        try {
            setLoading(true);
            const response = await serverClient.get('/api/user/settings');
            setSettings(response?.data?.settings);
            setNotification(
                response?.data?.settings === null ? true : response?.data?.settings?.notifications
            );
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError('userNotAuthorized');
                navigate('/login');
            } else {
                setError('somethingWentWrong');
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        feather.replace();
        fetchUserSettings();
    }, [fetchUserSettings]);

    useRefreshSignal(refreshSignal, () => {
        fetchUserSettings();
    });

    const handleTogglePaymentNotification = async () => {
        setNotification(!notification);
        try {
            setLoading(true);
            await serverClient.put('/api/user/settings', {
                notification: !notification,
            });
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError('userNotAuthorized');
                navigate('/login');
            } else {
                setError('somethingWentWrong');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageChange = async (e) => {
        const newLanguage = e.target.value;
        changeLanguage(newLanguage);
        localStorage.setItem('language', newLanguage);

        // Trigger language change event
        window.dispatchEvent(
            new CustomEvent('languageChange', {
                detail: { language: newLanguage },
            })
        );

        try {
            await serverClient.put('/api/user/settings', {
                language: newLanguage,
            });
        } catch (err) {
            if (err.response?.status === 401) {
                DeleteToken();
                setError('userNotAuthorized');
                navigate('/login');
            } else {
                setError('somethingWentWrong');
            }
        }
    };

    return (
        <div className="setting-page">
            <Helmet>
                <title>{getPageMeta('settings', language).title}</title>
                <meta name="description" content={getPageMeta('settings', language).description} />
                {getPageMeta('settings', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}
            </Helmet>
            <Loader loading={loading} />
            <header className="section-t-space">
                <div className="custom-container">
                    <div className="header-panel">
                        <Link to="/profile" className="back-btn">
                            <i className="icon" data-feather="arrow-left"></i>
                        </Link>
                        <h2>{t('settings')}</h2>
                    </div>
                </div>
            </header>

            <section className="section-b-space">
                <div className="custom-container">
                    {error && (
                        <div className="p-4">
                            <Alert alert={'danger'}>{t(error)}</Alert>
                        </div>
                    )}

                    {/* Notifications Section */}
                    <div className="settings-section">
                        <div className="settings-header">
                            <div className="settings-icon">
                                <i className="icon" data-feather="bell"></i>
                            </div>
                            <h3 className="settings-title">{t('notifications')}</h3>
                        </div>

                        <div className="settings-content">
                            <div className="setting-item">
                                <div className="setting-info">
                                    <h4>{t('pushNotifications')}</h4>
                                    <p className="setting-description">{t('getNotified')}</p>
                                </div>
                                <div className="switch-btn">
                                    <input
                                        type="checkbox"
                                        id="notification-toggle"
                                        checked={notification}
                                        onChange={handleTogglePaymentNotification}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Language Section */}
                    <div className="settings-section">
                        <div className="settings-header">
                            <div className="settings-icon language">
                                <i className="icon" data-feather="globe"></i>
                            </div>
                            <h3 className="settings-title">{t('languageRegion')}</h3>
                        </div>

                        <div className="settings-content">
                            <div className="setting-item">
                                <div className="setting-info">
                                    <h4>{t('systemLanguage')}</h4>
                                    <p className="setting-description">{t('chooseLanguage')}</p>
                                </div>
                                <div className="language-dropdown-wrapper">
                                    <select
                                        value={language}
                                        onChange={handleLanguageChange}
                                        className="language-dropdown"
                                    >
                                        <option value="en">English</option>
                                        <option value="bn">বাংলা</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Install App Section */}
                    <PWAInstallSettingsSection />

                    {/* Security Section */}
                    <div className="settings-section">
                        <div className="settings-header">
                            <div className="settings-icon lock">
                                <i className="icon" data-feather="lock"></i>
                            </div>
                            <h3 className="settings-title">{t('securityPrivacy')}</h3>
                        </div>

                        <div className="settings-content">
                            <div className="setting-item">
                                <div className="setting-info">
                                    <h4>{t('changePassword')}</h4>
                                    <p className="setting-description">
                                        {t('updatePasswordRegularly')}
                                    </p>
                                </div>
                                <Link to="/password-update" className="settings-action">
                                    <i className="icon" data-feather="arrow-right"></i>
                                </Link>
                            </div>

                            <div className="setting-item">
                                <div className="setting-info">
                                    <h4>{t('twoFactorAuth')}</h4>
                                    <p className="setting-description">{t('extraSecurityLayer')}</p>
                                </div>
                                <div className="settings-badge coming-soon">{t('soon')}</div>
                            </div>
                        </div>
                    </div>

                    {/* Help Section */}
                    <div className="settings-section">
                        <div className="settings-header">
                            <div className="settings-icon help">
                                <i className="icon" data-feather="help-circle"></i>
                            </div>
                            <h3 className="settings-title">{t('helpSupport')}</h3>
                        </div>

                        <div className="settings-content">
                            <Link to="/help-center" className="setting-item">
                                <div className="setting-info">
                                    <h4>{t('helpCenter')}</h4>
                                    <p className="setting-description">{t('findAnswers')}</p>
                                </div>
                                <i className="icon" data-feather="arrow-right"></i>
                            </Link>

                            {/*<Link to="#" className="setting-item">*/}
                            {/*    <div className="setting-info">*/}
                            {/*        <h4>Contact Us</h4>*/}
                            {/*        <p className="setting-description">Reach out to our support team</p>*/}
                            {/*    </div>*/}
                            {/*    <i className="icon" data-feather="arrow-right"></i>*/}
                            {/*</Link>*/}
                        </div>
                    </div>
                </div>
            </section>
            <BottomNavbar />
        </div>
    );
}

export default SettingPage;
